// Orders table access — Phase B.
//
// Expected `orders` schema (Supabase):
//   id             uuid  PK  DEFAULT gen_random_uuid()
//   user_id        uuid  FK → auth.users(id)  DEFAULT auth.uid()
//   order_number   text  UNIQUE  DEFAULT nextval → 'GL-####' (trigger/sequence)
//   service        enum  ('wash_fold' | 'same_day' | 'dry_cleaning' | 'members')
//   bags           text
//   pickup_date    date
//   pickup_slot    enum  ('morning' | 'evening')
//   return_date    date         NULL
//   return_slot    enum         NULL
//   return_tbd     boolean      DEFAULT false
//   address_line1  text
//   address_line2  text
//   city           text
//   state          text
//   zip            text
//   notes          text
//   created_at     timestamptz  DEFAULT now()
//
// RLS: users select/insert where user_id = auth.uid(). Never send
// user_id or order_number from the client — the DB fills them.

import { supabase } from './supabase'
import { addDays } from './dates'
import { notifyNewOrder } from './telegramAlerts'
import { zoneForZip } from './serviceArea'

// --- Insert / read ---------------------------------------------------------

export async function saveOrder(order) {
  const payload = {
    service: order.service,
    // service_id lets the DB join back to the services row for pricing.
    ...(order.service_id ? { service_id: order.service_id } : {}),
    bags: String(order.bags),
    pickup_date: order.pickupDate,
    pickup_slot: order.pickupSlot,
    return_date: order.returnDate || null,
    return_slot: order.returnSlot || null,
    return_tbd: !!order.returnDeferred,
    address_line1: order.address?.street || null,
    address_line2: order.address?.apt || null,
    city: order.address?.city || null,
    state: order.address?.state || null,
    zip: order.address?.zip || null,
    // Hotel + room stored at the order level so admin sees exactly what
    // the customer typed at booking time, even if their profile changes.
    hotel_name: order.address?.hotelName || null,
    room_number: order.address?.roomNumber || null,
    notes: order.notes || null,
    // The pricing zone is FROZEN here, from the pickup ZIP. If the Ponte
    // Vedra grid changes in November, this October order keeps the prices
    // it was quoted — nothing is ever recomputed from the live grid.
    pricing_zone_key: zoneForZip(order.address?.zip),
  }
  const { data, error } = await supabase
    .from('orders')
    .insert(payload)
    .select()
    .single()
  if (error) throw error
  // Telegram alert 3 — one trigger for every order, customer or staff.
  // Fire-and-forget: a failed alert must never fail a booking.
  notifyNewOrder(data?.id)
  return data
}

// Several services picked up in one go — one order per service, created in a
// single transaction by the create_pickup_group RPC. Either they all exist or
// none does; a half pickup can't happen. The group id and the "everything
// returns together" rule are decided server-side, never by the client.
export async function createPickupGroup(orders) {
  const { data, error } = await supabase.rpc('create_pickup_group', { p_orders: orders })
  if (error) throw error
  return data || []
}

export async function listOrders({ limit = 25 } = {}) {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

// Look up by the human-readable order_number (e.g. "GL-4821"). RLS scopes
// it to the current user automatically.
export async function getOrder(orderNumber) {
  if (!orderNumber) return null
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('order_number', orderNumber)
    .maybeSingle()
  if (error) throw error
  return data
}

// --- Business rules (unchanged from Phase A) -------------------------------

export const SERVICE_LABEL = {
  wash_fold: 'Wash & Fold',
  same_day: 'Same-Day Wash & Fold',
  dry_cleaning: 'Dry Cleaning',
  members: 'Ghost Members Wash & Fold',
}

// A price only exists in one place: the `services` table. Anything that
// needs a rate, minimum, or handling percentage reads it there (either
// via getService or off admin_orders_view) and formats it with fmtMoney.
// The old SERVICE_MIN / HANDLING_NOTE constants have been removed — they
// duplicated production values and drifted the moment prices changed.

// Minimum return DATE (ISO) accepted for a given pickup + service.
// Spec §4:
//   Wash & Fold / Members       → +24h min
//   Same-Day, morning pickup    → SAME day (evening return)
//   Same-Day, evening pickup    → next morning
//   Wash & Press / Dry Cleaning → no picker at all (returns null)
export function minReturnDate(service, pickupDate, pickupSlot) {
  if (!pickupDate) return null
  if (service === 'dry_cleaning' || service === 'wash_press') return null
  if (service === 'same_day' && pickupSlot === 'morning') return pickupDate
  return addDays(pickupDate, 1)
}

// Which return slots are allowed on `returnDate` given the pickup + service.
export function allowedReturnSlots(service, pickupDate, pickupSlot, returnDate) {
  if (service === 'dry_cleaning' || service === 'wash_press') return []
  if (!pickupDate || !pickupSlot || !returnDate) return ['morning', 'evening']
  const min = minReturnDate(service, pickupDate, pickupSlot)
  if (min && returnDate > min) return ['morning', 'evening']
  if (service === 'same_day') {
    // Same-Day, morning pickup → only evening the same day.
    if (pickupSlot === 'morning' && returnDate === pickupDate) return ['evening']
    // Same-Day, evening pickup → only morning the next day.
    if (pickupSlot === 'evening' && returnDate === addDays(pickupDate, 1)) return ['morning']
    return ['morning', 'evening']
  }
  // wash_fold / members: 24h rule → evening pickup blocks morning next day
  if (pickupSlot === 'evening') return ['evening']
  return ['morning', 'evening']
}

export function computeReturn(service, pickupDate, pickupSlot) {
  if (!pickupDate || !pickupSlot) return { date: '', slot: '', deferred: false, label: '' }
  if (service === 'dry_cleaning' || service === 'wash_press') {
    return { date: '', slot: '', deferred: true, label: 'Confirmed when ready' }
  }
  if (service === 'same_day') {
    // Morning pickup → same day evening. Evening pickup → next morning.
    if (pickupSlot === 'morning') {
      return { date: pickupDate, slot: 'evening', deferred: false, label: 'tonight · 6–9 PM' }
    }
    return { date: addDays(pickupDate, 1), slot: 'morning', deferred: false, label: 'tomorrow morning · 9–11 AM' }
  }
  return {
    date: addDays(pickupDate, 1),
    slot: pickupSlot,
    deferred: false,
    label: pickupSlot === 'morning'
      ? 'in 24–48 hours · 9–11 AM'
      : 'in 24–48 hours · 6–9 PM',
  }
}
